/**
 * Módulo para gerenciamento de seleção de tenant baseado em querystring e localStorage
 */

import {
  DEFAULT_REVENDA_HOST_PATTERNS,
  getRealm,
  isThirdPartyTenant,
} from './realm';
import { detectBrowserLanguage, isSupportedLang } from './lang';

// Constantes
const QUERY_PARAM_NAME = 'tenant';
const USER_PREFERENCES_KEY = 'user-preferences';
const LANG_PARAM_NAME = 'lang';
const REDIRECT_TO_PARAM_NAME = 'redirectTo';
const LOCAL_STORAGE_KEY = 'selectedTenant';
const REALM_STORAGE_KEY = 'realm';

// Interfaces
interface TenantConfig {
  tenants: string[];
  default?: string;
}

interface AvailableTenants {
  [hostname: string]: string[] | TenantConfig;
}

interface TenantSelectionOptions {
  availableTenants: AvailableTenants;
  tenantSelectionUrl: string;
  /** Padrões de hostname que indicam realm `third`. Default: `['revenda']`. */
  revendaHostPatterns?: string[];
}

interface TenantSelectionResult {
  tenant: string;
  realm: string;
}

/**
 * Inicializa a seleção de tenant baseado em querystring ou localStorage
 *
 * @param options - Configurações contendo availableTenants e tenantSelectionUrl
 * @param saveRealm - Salva realm no localStorage (use false para quando a tela de selecao de realm esteja ativa)
 * @returns Objeto contendo tenant e realm válidos selecionados ou null se houve redirecionamento
 */
export function initializeTenantSelection(
  options: TenantSelectionOptions,
  saveRealm: boolean = true,
): TenantSelectionResult | null {
  const {
    availableTenants,
    tenantSelectionUrl,
    revendaHostPatterns = DEFAULT_REVENDA_HOST_PATTERNS,
  } = options;

  /**
   * Extrai o valor do parâmetro tenant da querystring
   */
  function getTenantFromQuery(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    const tenant = urlParams.get(QUERY_PARAM_NAME);
    return tenant ? tenant.trim() : null;
  }

  /**
   * Extrai o valor do parâmetro redirectTo da querystring
   */
  function getRedirectToFromQuery(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    const redirectTo = urlParams.get(REDIRECT_TO_PARAM_NAME);
    return redirectTo ? redirectTo.trim() : null;
  }

  function getTenantsArray(config: string[] | TenantConfig): string[] {
    if (Array.isArray(config)) {
      return config;
    } else {
      return Array.isArray(config.tenants) ? config.tenants : [];
    }
  }

  function getDefaultTenant(config: string[] | TenantConfig): string | null {
    const tenants = getTenantsArray(config);
    if (tenants.length === 1) {
      return tenants[0];
    }
    if (!Array.isArray(config) && config.default) {
      return config.default;
    }

    // Heurística: em hostnames de revenda (third-party), seleciona o tenant -3pd do país
    const hostname = getCurrentHostname();
    if (getRealm(hostname, revendaHostPatterns) === 'third') {
      return tenants.find(t => isThirdPartyTenant(t)) ?? null;
    }

    return null;
  }

  // Função auxiliar para obter hostname atual
  function getCurrentHostname(): string {
    try {
      const url = new URL(window.location.href);
      return url.hostname;
    } catch {
      return window.location.hostname;
    }
  }

  /**
   * Navega para a URL de redirecionamento sem alterar o histórico de navegação
   * Valida se a URL é segura (mesmo origin ou relativa)
   * Remove os parâmetros tenant e lang da URL de destino para evitar conflitos
   */
  function navigateToRedirectUrl(redirectUrl: string): void {
    try {
      // Validar se é URL segura (mesmo origin ou relativa)
      const url = new URL(redirectUrl, window.location.origin);
      if (url.origin !== window.location.origin) {
        console.warn(
          'Redirecionamento para origem externa bloqueado:',
          redirectUrl,
        );
        return;
      }

      // Remover parâmetros tenant e lang da URL de destino para evitar conflitos
      url.searchParams.delete(QUERY_PARAM_NAME);
      url.searchParams.delete('lang');

      // Navegar usando replaceState (sem alterar histórico)
      window.history.replaceState({}, document.title, url.toString());
    } catch (error) {
      console.warn('Erro ao processar URL de redirecionamento:', error);
    }
  }

  /**
   * Recupera o tenant salvo no localStorage
   */
  function getTenantFromLocalStorage(): string | null {
    try {
      return localStorage.getItem(LOCAL_STORAGE_KEY);
    } catch (error) {
      console.warn('Erro ao acessar localStorage:', error);
      return null;
    }
  }

  /**
   * Verifica se o hostname atual possui apenas um tenant disponível ou possui um tenant padrão definido
   */
  function hasOnlyOneTenant(availableTenants: AvailableTenants): string | null {
    const currentHostname = getCurrentHostname();
    const config = availableTenants[currentHostname];
    if (!config) return null;
    return getDefaultTenant(config);
  }

  /**
   * Valida se o tenant é válido para o hostname atual
   */
  function isValidTenant(
    tenant: string,
    availableTenants: AvailableTenants,
  ): boolean {
    let currentHostname: string;
    try {
      const url = new URL(window.location.href);
      currentHostname = url.hostname;
    } catch {
      currentHostname = window.location.hostname;
    }

    const config = availableTenants[currentHostname];
    if (!config) return false;

    return getTenantsArray(config).includes(tenant);
  }

  /**
   * Salva o tenant no localStorage
   */
  function saveTenantToLocalStorage(
    tenant: string,
    saveRealm: boolean,
  ): void {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, tenant);
      // Salvar flag realm baseada nas regras de negócio
      if (saveRealm) {
        saveRealmFlag();
      }
    } catch (error) {
      console.warn('Erro ao salvar no localStorage:', error);
    }
  }

  /**
   * Salva a flag "realm" no localStorage baseada no nome do tenant e hostname
   */
  function saveRealmFlag(): void {
    try {
      // Obter hostname atual
      const currentHostname = getCurrentHostname();

      const realmValue = getRealm(currentHostname, revendaHostPatterns);
      localStorage.setItem(REALM_STORAGE_KEY, realmValue);
    } catch (error) {
      console.warn('Erro ao salvar flag realm no localStorage:', error);
    }
  }

  /**
   * Remove os parâmetros tenant e lang da URL sem alterar o histórico de navegação
   */
  function removeQueryFromUrl(): void {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete(QUERY_PARAM_NAME);
    urlParams.delete('lang');

    const url = new URL(window.location.href);
    const queryString = urlParams.toString();
    const newUrl = `${url.origin}${url.pathname}${
      queryString ? '?' + queryString : ''
    }${url.hash}`;

    // Usa replaceState para não alterar o histórico
    window.history.replaceState({}, document.title, newUrl);
  }

  /**
   * Redireciona o usuário para a URL de seleção de tenant
   * Adiciona um parâmetro redirectTo com o path + querystring + hash da URL atual
   */
  function getFallbackTenant(): TenantSelectionResult | null {
    const hostname = getCurrentHostname();
    const config = availableTenants[hostname];
    if (!config) {
      return null;
    } else {
      const tenants = getTenantsArray(config);
      if (!tenants.length) {
        return null;
      } else {
        const tenant = tenants[0];
        saveTenantToLocalStorage(tenant, saveRealm);
        return { tenant, realm: getRealm(hostname, revendaHostPatterns) };
      }
    }
  }

  function redirectToTenantSelection(url: string): void {
    const currentUrl = new URL(window.location.href);

    // Criar o valor do redirectTo com pathname + search + hash
    const redirectToValue =
      currentUrl.pathname + currentUrl.search + currentUrl.hash;

    // Se a URL é absoluta, criar nova URL e adicionar apenas o parâmetro redirectTo
    if (url.startsWith('http')) {
      const tenantSelectionUrl = new URL(url);
      tenantSelectionUrl.searchParams.set('redirectTo', redirectToValue);
      window.location.href = tenantSelectionUrl.toString();
      return;
    }

    // Para URLs relativas, usar URL constructor e adicionar apenas o parâmetro redirectTo
    const redirectUrl = new URL(url, window.location.origin);
    redirectUrl.searchParams.set('redirectTo', redirectToValue);

    window.location.href = redirectUrl.toString();
  }

  /**
   * Lê o parâmetro `lang` da querystring e persiste em `user-preferences`
   * (formato { theme?, lang }) ANTES que o parâmetro seja removido da URL,
   * para que a aplicação inicialize no idioma indicado pelo deep-link.
   * Idiomas não suportados ou ausentes são ignorados (preserva a preferência atual).
   */
  function persistLangFromQuery(): void {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const lang = urlParams.get(LANG_PARAM_NAME)?.trim();
      if (!isSupportedLang(lang)) return;

      let preferences: Record<string, unknown> = {};
      const raw = localStorage.getItem(USER_PREFERENCES_KEY);
      if (raw) {
        try {
          preferences = JSON.parse(raw) as Record<string, unknown>;
        } catch {
          preferences = {};
        }
      }

      localStorage.setItem(
        USER_PREFERENCES_KEY,
        JSON.stringify({ ...preferences, lang }),
      );
    } catch (error) {
      console.warn('Erro ao persistir idioma da querystring:', error);
    }
  }

  /**
   * Quando não há idioma no deep-link nem preferência salva, persiste o idioma
   * do navegador (se suportado) em `user-preferences`, para que a aplicação
   * inicialize no idioma do navegador em vez do default de ambiente.
   * Uma preferência de idioma já existente (deep-link ou escolha do usuário) é
   * sempre preservada.
   */
  function persistBrowserLangFallback(): void {
    try {
      let preferences: Record<string, unknown> = {};
      const raw = localStorage.getItem(USER_PREFERENCES_KEY);
      if (raw) {
        try {
          preferences = JSON.parse(raw) as Record<string, unknown>;
        } catch {
          preferences = {};
        }
      }

      // Já há preferência de idioma válida: preserva
      if (isSupportedLang(preferences.lang as string | undefined)) return;

      const detected = detectBrowserLanguage();
      if (!detected) return;

      localStorage.setItem(
        USER_PREFERENCES_KEY,
        JSON.stringify({ ...preferences, lang: detected }),
      );
    } catch (error) {
      console.warn('Erro ao persistir idioma do navegador:', error);
    }
  }

  // Persistir idioma do deep-link antes de qualquer remoção da querystring
  persistLangFromQuery();
  // Fallback: sem deep-link e sem preferência salva, usar o idioma do navegador
  persistBrowserLangFallback();

  // 0. VERIFICAR SE EXISTE PARÂMETRO redirectTo PARA NAVEGAÇÃO DE RETORNO
  const queryTenant = getTenantFromQuery();
  const redirectTo = getRedirectToFromQuery();
  if (redirectTo) {
    // Navegar para a URL de redirecionamento sem alterar o histórico
    navigateToRedirectUrl(redirectTo);
    // Continuar com a lógica normal após a navegação
  }

  // 1. NOVA REGRA: Se hostname possui apenas um tenant (ou heurística revenda),
  // retornar automaticamente — mas SOMENTE quando não há ?tenant= na URL.
  // Query param explícito tem prioridade sobre a heurística de auto-seleção.
  const singleTenant = !queryTenant ? hasOnlyOneTenant(availableTenants) : null;
  if (singleTenant) {
    // Salvar flag realm para tenant único
    if (saveRealm) {
      saveRealmFlag();
    }
    // Remover parâmetro tenant da URL se existir
    removeQueryFromUrl();
    // Retornar objeto com tenant e realm
    const currentHostname = getCurrentHostname();
    return {
      tenant: singleTenant,
      realm: getRealm(currentHostname, revendaHostPatterns),
    };
  }

  // 2. Verificar se existe querystring com tenant

  if (queryTenant) {
    // Validar tenant da querystring
    if (isValidTenant(queryTenant, availableTenants)) {
      // Tenant válido: salvar no localStorage e remover querystring
      saveTenantToLocalStorage(queryTenant, saveRealm);
      removeQueryFromUrl();
      // Retornar objeto com tenant e realm
      const currentHostname = getCurrentHostname();
      return {
        tenant: queryTenant,
        realm: getRealm(currentHostname, revendaHostPatterns),
      };
    } else {
      // Tenant inválido: redirecionar para seleção ou usar fallback
      if (!tenantSelectionUrl) return getFallbackTenant();
      redirectToTenantSelection(tenantSelectionUrl);
      return null;
    }
  }

  // 3. Não há querystring: verificar localStorage
  const storedTenant = getTenantFromLocalStorage();

  if (storedTenant) {
    // Validar tenant do localStorage
    if (isValidTenant(storedTenant, availableTenants)) {
      // Tenant válido: atualizar flag realm e retornar
      if (saveRealm) {
        saveRealmFlag();
      }
      // Retornar objeto com tenant e realm
      const currentHostname = getCurrentHostname();
      return {
        tenant: storedTenant,
        realm: getRealm(currentHostname, revendaHostPatterns),
      };
    } else {
      // Tenant inválido: limpar localStorage e redirecionar ou usar fallback
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      if (!tenantSelectionUrl) return getFallbackTenant();
      redirectToTenantSelection(tenantSelectionUrl);
      return null;
    }
  }

  // 4. Não há querystring nem localStorage: redirecionar para seleção ou usar fallback
  if (!tenantSelectionUrl) return getFallbackTenant();
  redirectToTenantSelection(tenantSelectionUrl);
  return null;
}

/**
 * Remove o tenant do localStorage e redireciona para a URL de seleção de tenant
 * @param tenantSelectionUrl URL para a página de seleção de tenant
 */
export function clearTenantAndRedirect(tenantSelectionUrl: string): void {
  try {
    // Remover tenant do localStorage
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  } catch (error) {
    console.warn('Erro ao limpar tenant e redirecionar:', error);
  }

  // Redirecionar para a URL de seleção de tenant (sempre executar, mesmo se houver erro no localStorage)
  try {
    const currentUrl = new URL(window.location.href);

    // Criar o valor do redirectTo com pathname + search + hash
    const redirectToValue =
      currentUrl.pathname + currentUrl.search + currentUrl.hash;

    // Se a URL é absoluta, criar nova URL e adicionar apenas o parâmetro redirectTo
    if (tenantSelectionUrl.startsWith('http')) {
      const selectionUrl = new URL(tenantSelectionUrl);
      selectionUrl.searchParams.set('redirectTo', redirectToValue);
      window.location.href = selectionUrl.toString();
      return;
    }

    // Para URLs relativas, usar URL constructor e adicionar apenas o parâmetro redirectTo
    const redirectUrl = new URL(tenantSelectionUrl, window.location.origin);
    redirectUrl.searchParams.set('redirectTo', redirectToValue);

    window.location.href = redirectUrl.toString();
  } catch (error) {
    console.warn('Erro ao redirecionar:', error);
  }
}

// Exportar tipos para uso externo
export type { AvailableTenants, TenantSelectionOptions, TenantSelectionResult };
